import './page.scss'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import {
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material'
import {
  CSSProperties,
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import KumocanButton from 'components/button'
import KumocanDialog from 'components/dialog'
import { notificationApi } from 'hooks/api/notification'
import { convertToLink } from 'hooks/helper/link'
import { useSnackbar } from 'hooks/useSnackbar/SnackbarContext'
import NotificationFilesComponent from 'pages/notification/components/notificationFiles'
import { AllianceAssign, Assign, ASSIGN_STATUS } from 'types/assign'
import { Notification, notificationStatus } from 'types/notification'
import { OrderWithAssign } from 'types/order'
import { BLUE_COLOR, PRIMARY_COLOR } from 'valiable'

// 配置が削除された場合の表示コンポーネント
function DeletedAssignCard({
  contractDetailName,
  securitySiteLabel,
}: {
  contractDetailName: string
  securitySiteLabel: string
}) {
  return (
    <div style={deletedCardContainerStyle}>
      <p style={deletedCardLabelStyle}>{securitySiteLabel}</p>
      <p style={deletedCardNameStyle}>{contractDetailName}</p>
    </div>
  )
}

// NOTE 配置の返答ページ
export default function AssignConfirmPage() {
  const { t } = useTranslation([
    'page-notification-confirm',
    'page-assign-detail',
    'common',
  ])
  const { showSnackbar } = useSnackbar()
  const [totalVolume, setTotalVolume] = useState<number>()
  const [qualfiedVolume, setQualifiedVolume] = useState<number>()
  const [nonQualfiedVolume, setNonQualifiedVolume] = useState<number>()
  const [order, setOrder] = useState<OrderWithAssign>()
  const [replyFomat, setReplyFomat] = useState<string>()
  const [res, setRes] = useState<number>(ASSIGN_STATUS.AVAILABLE_FOR_WORK)
  const [replyWhenUnavailable, setReplyWhenUnavailable] = useState<string>('')
  const [replyWhenAvailable, setReplyWhenAvailable] = useState<string>('')
  const [otherAssigns, setOtherAssigns] = useState<Assign[]>([])
  const [otherAllianceAssigns, setOtherAllianceAssigns] = useState<
    AllianceAssign[]
  >([])
  const [open, setOpen] = useState<boolean>(false)
  const [notification, setNotification] = useState<Notification>()
  const [replyWhenUnavailableErr, setReplyWhenUnavailableErr] =
    useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const responseFormRef = useRef<HTMLDivElement>(null)

  const { id } = useParams<{ id: string }>()
  const fetchData = useCallback(() => {
    setIsLoading(true)
    const noti = notificationApi()
      .findById(id!)
      .then((res) => {
        setNotification(res.data)
        if (res.data.status === notificationStatus.Notified) {
          notificationApi().readed(id!)
        }
      })

    const assign = notificationApi()
      .findAssignById(id!)
      .then((res) => {
        setTotalVolume(res.data.volume)
        setQualifiedVolume(res.data.qualifiedVolume)
        setNonQualifiedVolume(res.data.nonQualifiedVolume)
        setOrder(res.data.order)
        setReplyFomat(res.data.replyFomat)
        setReplyWhenAvailable(res.data.replyFomat)
        setOtherAssigns(res.data.otherAssigns)
        setOtherAllianceAssigns(res.data.otherAllianceAssigns)
      })

    Promise.all([noti, assign])
      .catch((err) => {
        console.error('Error fetching data:', err)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [id])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // 回答済みの時は、5分ごとにデータを更新
  useEffect(() => {
    if (res === ASSIGN_STATUS.AVAILABLE_FOR_WORK) {
      const fetchTimer = setInterval(
        () => {
          fetchData()
        },
        5 * 60 * 1000,
      )

      // resが変わったりコンポーネントがアンマウントされたら解除
      return () => clearInterval(fetchTimer)
    }
    // res が AVAILABLE_FOR_WORK でないときはタイマーなし
    return
  }, [res, fetchData])

  const isDisabled = (): boolean => {
    if (res === 0) return true
    if (order?.assign.status !== ASSIGN_STATUS.NOTIFIED) return true
    return false
  }

  const isCancel = () => {
    return (
      order?.assign.id === '' ||
      order?.assign.status === ASSIGN_STATUS.COMPLETED ||
      !order
    )
  }

  const statusLabel = () => {
    if (isLoading) {
      return (
        <Alert sx={{ fontSize: '20px' }} severity="info">
          {t('common:loading')}
        </Alert>
      )
    }
    // 配置が紐づいてない,または配置が配置済みに戻された時はキャンセルされたものとして扱う
    if (isCancel()) {
      return (
        <Alert severity="warning" sx={alertStyle}>
          {t('status.cancelled')}
        </Alert>
      )
    }
    switch (order?.assign.status) {
      case ASSIGN_STATUS.NOTIFIED:
        return (
          <Alert severity="info" sx={alertStyle}>
            {t('status.assigned')}
            <br />
            {t('status.assigned_action')}
          </Alert>
        )
      case ASSIGN_STATUS.DELETED:
        return (
          <Alert severity="warning" sx={alertStyle}>
            {t('status.deleted')}
          </Alert>
        )
      case ASSIGN_STATUS.AVAILABLE_FOR_WORK:
        return (
          <Alert severity="success" sx={alertStyle}>
            {t('status.answered')}
            <br />
            <p>
              {t('answer_label')} {t('common:response.confirmed')}
            </p>
            <p>
              {t('reply_comment_label')} {order.assign.replyWhenAvailable}
            </p>
          </Alert>
        )
      case ASSIGN_STATUS.UNAVAILABLE_FOR_WORK:
        return (
          <Alert severity="warning">
            {t('status.answered')}
            <br />
            <p>
              {t('answer_label')} {t('common:response.report')}
            </p>
            <p>
              {t('reason_label')} {order.assign.replyWhenUnavailable}
            </p>
          </Alert>
        )
    }
  }

  const handleResChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReplyWhenUnavailable('')
    setReplyWhenAvailable('')
    setRes(Number((event.target as HTMLInputElement).value))
  }

  const handleReplyWhenUnavailable = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const data = event.target.value.trim()
    setReplyWhenUnavailable(data)
  }

  const handleReplyChange = (event: ChangeEvent<HTMLInputElement>) => {
    const data = event.target.value.trim()
    setReplyWhenAvailable(data)
  }

  const handleOepn = () => {
    if (res === ASSIGN_STATUS.UNAVAILABLE_FOR_WORK && !replyWhenUnavailable) {
      setReplyWhenUnavailableErr(t('report_required'))
      return
    }
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const submit = () => {
    notificationApi()
      .replyAssignPossibility(
        id!,
        res,
        replyWhenAvailable,
        replyWhenUnavailable,
      )
      .then(() => {
        handleClose()
        fetchData()
      })
  }

  const toMap = () => {
    if (!order?.address) {
      showSnackbar(t('common:messages.address_not_registered'))
    } else if (order?.lat !== 0 && order?.lng !== 0) {
      // 緯度経度が存在する場合、緯度経度でGoogleマップを開く
      location.href = `https://www.google.com/maps/search/?api=1&query=${order?.lat},${order?.lng}&launch_app=true`
    } else {
      // 緯度経度が0の場合は住所でGoogleマップを開く
      location.href = `https://www.google.com/maps/search/?api=1&query=${order?.address}&launch_app=true`
    }
  }

  const {
    qualifiedAssigns,
    nonQualifiedAssigns,
    qualifiedAllianceAssigns,
    nonQualifiedAllianceAssigns,
    qualifiedPendingCount,
    nonQualifiedPendingCount,
  } = useMemo(() => {
    // ----- 配置済みの人員を分類する,隊長を先頭に -----
    const qualifiedAssigns = otherAssigns
      .filter((a) => a.status !== ASSIGN_STATUS.DELETED && !!a.hasQualified)
      .sort((a, b) => Number(b.isLeader) - Number(a.isLeader))
    const nonQualifiedAssigns = otherAssigns
      .filter((a) => a.status !== ASSIGN_STATUS.DELETED && !a.hasQualified)
      .sort((a, b) => Number(b.isLeader) - Number(a.isLeader))

    // ----- 配置済みの協力会社を分類する -----
    const qualifiedAllianceAssigns = otherAllianceAssigns.filter(
      (a) => !!a.hasQualified,
    )
    const nonQualifiedAllianceAssigns = otherAllianceAssigns.filter(
      (a) => !a.hasQualified,
    )

    // 配置予定人数に対して、まだ未確定(足りていない人数)を計算して「確認中」を表示するための数
    const qualifiedPendingCount = Math.max(
      (qualfiedVolume || 0) -
        qualifiedAssigns.length -
        qualifiedAllianceAssigns.length,
      0,
    )
    const nonQualifiedPendingCount = Math.max(
      (nonQualfiedVolume || 0) -
        nonQualifiedAssigns.length -
        nonQualifiedAllianceAssigns.length,
      0,
    )

    return {
      qualifiedAssigns: qualifiedAssigns,
      nonQualifiedAssigns: nonQualifiedAssigns,
      qualifiedAllianceAssigns: qualifiedAllianceAssigns,
      nonQualifiedAllianceAssigns: nonQualifiedAllianceAssigns,
      qualifiedPendingCount: qualifiedPendingCount,
      nonQualifiedPendingCount: nonQualifiedPendingCount,
    }
  }, [otherAssigns, otherAllianceAssigns, qualfiedVolume, nonQualfiedVolume])

  // 配置済みの時のみ回答できるように
  const showResponseContainer: boolean =
    order?.assign.status === ASSIGN_STATUS.NOTIFIED

  const scrollToResponse = () => {
    responseFormRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }

  return (
    <Box sx={pageContainerStyle}>
      <Box>
        <Typography component={'div'} sx={statusLabelContainerStyle}>
          {statusLabel()}
        </Typography>
        <Box sx={cardStyle}>
          {!isCancel() && order ? (
            <>
              {/* DELETED状態の場合は警備先のみ表示 */}
              {order.assign.status === ASSIGN_STATUS.DELETED ? (
                <DeletedAssignCard
                  contractDetailName={order.contractDetailName}
                  securitySiteLabel={t('page-assign-detail:security_site')}
                />
              ) : (
                <>
                  <div style={dateHeaderStyle}>
                    <p style={dateTextStyle}>
                      {order.date!.format(
                        t('common:date_format.date_with_day'),
                      )}{' '}
                      {order.startTime?.format('HH:mm')}~
                      {order.endTime?.format('HH:mm')}
                    </p>
                    {order.assign.loanInfo?.lendCompanyId && (
                      <p style={loanBadgeStyle}>
                        {order.assign.loanInfo.borrowCompanyName}/
                        {order.assign.loanInfo.borrowCompanyBranchName}
                      </p>
                    )}
                  </div>

                  <div style={columnDivStyle}>
                    <p style={columnTitleStyle}>
                      {t('page-assign-detail:client_name')}
                    </p>
                    <p>{order.clientName}</p>
                  </div>

                  <div style={columnDivStyle}>
                    <p style={columnTitleStyle}>
                      {t('page-assign-detail:security_site')}
                    </p>
                    <p>{order.contractDetailName}</p>
                  </div>

                  <div style={columnDivStyle}>
                    <p style={columnTitleStyle}>
                      {t('page-assign-detail:nearest_station')}
                    </p>
                    <p>{order.nearestStation}</p>
                  </div>

                  <div style={columnDivStyle}>
                    <p style={columnTitleStyle}>
                      {t('page-assign-detail:address')}
                    </p>
                    <div>
                      <p>{order.address}</p>
                      <div style={mapButtonContainerStyle}>
                        {' '}
                        <KumocanButton onClick={toMap}>
                          {t('common:buttons.show_map')}
                        </KumocanButton>
                      </div>
                    </div>
                  </div>
                  <div style={columnDivStyle}>
                    <p style={columnTitleStyle}>
                      {t('page-assign-detail:assigned_count')}
                    </p>
                    <p>
                      {t('page-assign-detail:people_count', {
                        count: totalVolume,
                      })}
                    </p>
                  </div>
                  <div style={columnDivStyle}>
                    <p style={columnTitleStyle}>
                      {t('page-assign-detail:workers')}
                    </p>
                    <div style={workerSectionStyle}>
                      <div>
                        <p style={hasQualifiedStyle}>
                          {t('page-assign-detail:non_qualified')}
                        </p>
                        <div style={arrayStyle}>
                          {nonQualifiedAssigns.map((a) => (
                            <Box sx={{ position: 'relative' }}>
                              {a.isLeader && (
                                <span style={leaderStyle}>
                                  {t('page-assign-detail:leader')}
                                </span>
                              )}
                              <div key={a.security.id} style={badgeStyle}>
                                {a.security.lastName} {a.security.firstName}
                                {a.loanInfo?.lendCompanyId && (
                                  <>
                                    {'/'}
                                    {a.loanInfo.lendCompanyName}/
                                    {a.loanInfo.lendCompanyBranchName}
                                  </>
                                )}
                              </div>
                            </Box>
                          ))}
                          {Array.from({ length: nonQualifiedPendingCount }).map(
                            (_, i) => (
                              <div
                                key={`nonq-pending-${i}`}
                                style={pendingStyle}
                              >
                                {t('page-assign-detail:pending')}
                              </div>
                            ),
                          )}
                        </div>
                        {nonQualifiedAllianceAssigns.length > 0 && (
                          <div style={allianceSectionStyle}>
                            <p style={allianceTextStyle}>
                              {t('page-assign-detail:alliance_company')}
                            </p>
                            <div style={arrayStyle}>
                              {nonQualifiedAllianceAssigns.map((a) => (
                                <div
                                  key={a.clientId}
                                  style={allianceBadgeStyle}
                                >
                                  {a.client.displayName}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      <div>
                        <p style={hasQualifiedStyle}>
                          {t('page-assign-detail:qualified')}
                        </p>
                        <div style={arrayStyle}>
                          {qualifiedAssigns.map((a) => (
                            <Box sx={{ position: 'relative' }}>
                              {a.isLeader && (
                                <span style={leaderStyle}>
                                  {t('page-assign-detail:leader')}
                                </span>
                              )}
                              <div key={a.security.id} style={badgeStyle}>
                                {a.security.lastName} {a.security.firstName}
                                {a.loanInfo?.lendCompanyId && (
                                  <>
                                    {'/'}
                                    {a.loanInfo.lendCompanyName}/
                                    {a.loanInfo.lendCompanyBranchName}
                                  </>
                                )}
                              </div>
                            </Box>
                          ))}
                          {Array.from({ length: qualifiedPendingCount }).map(
                            (_, i) => (
                              <div
                                key={`nonq-pending-${i}`}
                                style={pendingStyle}
                              >
                                {t('page-assign-detail:pending')}
                              </div>
                            ),
                          )}
                        </div>
                        {qualifiedAllianceAssigns.length > 0 && (
                          <div style={allianceSectionStyle}>
                            <p style={allianceTextStyle}>
                              {t('page-assign-detail:alliance_company')}
                            </p>
                            <div style={arrayStyle}>
                              {qualifiedAllianceAssigns.map((a) => (
                                <div
                                  key={a.clientId}
                                  style={allianceBadgeStyle}
                                >
                                  {a.client.displayName}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div style={columnDivStyle}>
                    <p style={commentTitleStyle}>
                      {t('page-assign-detail:comment_to_security')}
                    </p>
                    <Typography
                      sx={commentContentStyle}
                      dangerouslySetInnerHTML={{
                        __html: convertToLink(notification?.content || ''),
                      }}
                    ></Typography>
                  </div>
                  <div>
                    {/* 関連資料 */}
                    <NotificationFilesComponent
                      files={notification?.notificationFiles || []}
                    />
                  </div>
                </>
              )}
            </>
          ) : (
            <></>
          )}
        </Box>

        {/* 回答フォーム */}
        {showResponseContainer &&
          order?.assign.status !== ASSIGN_STATUS.DELETED && (
            <Box ref={responseFormRef} sx={responseFormContainerStyle}>
              <FormControl>
                <RadioGroup
                  aria-labelledby="demo-controlled-radio-buttons-group"
                  name="controlled-radio-buttons-group"
                  value={res}
                  onChange={handleResChange}
                  sx={radioGroupStyle}
                >
                  <FormControlLabel
                    value={ASSIGN_STATUS.AVAILABLE_FOR_WORK}
                    control={<Radio sx={radioStyle} />}
                    label={t('common:response.confirmed')}
                    sx={{ fontSize: '16px' }}
                  />

                  <FormControlLabel
                    value={ASSIGN_STATUS.UNAVAILABLE_FOR_WORK}
                    control={<Radio sx={radioStyle} />}
                    label={t('common:response.report')}
                    sx={{ fontSize: '16px' }}
                  />
                </RadioGroup>

                {res === ASSIGN_STATUS.UNAVAILABLE_FOR_WORK && (
                  <TextField
                    sx={textFieldStyle}
                    id="outlined-multiline-flexible"
                    label={t('common:response.report_content')}
                    multiline
                    maxRows={5}
                    required
                    onChange={handleReplyWhenUnavailable}
                    error={!!replyWhenUnavailableErr.trim()}
                  />
                )}

                {res === ASSIGN_STATUS.AVAILABLE_FOR_WORK && (
                  <TextField
                    label={t('common:response.reply_comment')}
                    sx={textFieldStyle}
                    id="outlined-multiline-flexible"
                    multiline
                    defaultValue={replyFomat}
                    maxRows={5}
                    onChange={handleReplyChange}
                  />
                )}

                {replyWhenUnavailableErr && (
                  <Typography sx={errorTextStyle}>
                    {replyWhenUnavailableErr}
                  </Typography>
                )}

                <KumocanButton
                  onClick={handleOepn}
                  style={submitButtonStyle}
                  disabled={isDisabled()}
                >
                  {t('common:buttons.submit_answer')}
                </KumocanButton>
              </FormControl>
            </Box>
          )}

        {/* 回答誘導矢印 */}
        {showResponseContainer &&
          order?.assign.status !== ASSIGN_STATUS.DELETED && (
            <Box
              sx={floatingArrowContainerStyle}
              onClick={scrollToResponse}
              role="button"
              aria-label={t('scroll_to_response')}
              tabIndex={0}
              className="floating-arrow"
            >
              <KeyboardArrowDownIcon sx={floatingArrowStyle} />
            </Box>
          )}

        <KumocanDialog open={open} handleClose={handleClose}>
          <Typography sx={dialogConfirmTextStyle}>
            {t('confirm_dialog')}
          </Typography>
          {res === ASSIGN_STATUS.AVAILABLE_FOR_WORK && (
            <table className="reply-table">
              <tbody>
                <tr>
                  <td>{t('answer_label')}</td>
                  <td>
                    <strong>{t('common:response.confirmed')}</strong>
                  </td>
                </tr>
                <tr>
                  <td>{t('reply_comment_label')}</td>
                  <td>{replyWhenAvailable}</td>
                </tr>
              </tbody>
            </table>
          )}
          {res === ASSIGN_STATUS.UNAVAILABLE_FOR_WORK && (
            <table className="reply-table">
              <tbody>
                <tr>
                  <td>{t('answer_label')}</td>
                  <td>
                    <strong style={reportTextStyle}>
                      {t('common:response.report')}
                    </strong>
                  </td>
                </tr>
                <tr>
                  <td>{t('reason_label')}</td>
                  <td>{replyWhenUnavailable}</td>
                </tr>
              </tbody>
            </table>
          )}
          <Box sx={dialogButtonContainerStyle}>
            <KumocanButton onClick={handleClose} variant="outlined">
              {t('common:buttons.no')}
            </KumocanButton>
            <KumocanButton onClick={submit} style={dialogYesButtonStyle}>
              {t('common:buttons.yes')}
            </KumocanButton>
          </Box>
        </KumocanDialog>
      </Box>
    </Box>
  )
}

// スタイル定義

const cardStyle: CSSProperties = {
  width: '90%',
  margin: '0px auto 20px',
  padding: '0px 20px 20px',
  borderRadius: '10px',
}

const submitButtonStyle: CSSProperties = {
  width: '100%',
  height: '50px',
  fontSize: '18px',
  margin: '20px auto 0',
  borderRadius: '10px',
}

const pageContainerStyle = {
  paddingBottom: '300px',
}

const statusLabelContainerStyle = {
  display: 'block',
  width: '85%',
  margin: '20px auto',
  fontSize: '16px',
  color: '#333',
  lineHeight: '1.6',
}

const dateHeaderStyle: CSSProperties = {
  textAlign: 'left',
  marginBottom: '10px',
  borderBottom: '1px dotted #ccc',
  paddingBottom: '10px',
}

const dateTextStyle: CSSProperties = {
  fontWeight: 'bold',
}

const loanBadgeStyle: CSSProperties = {
  margin: '5px 0',
  padding: '0px 5px',
  backgroundColor: BLUE_COLOR,
  display: 'inline-block',
  color: 'white',
}

const mapButtonContainerStyle: CSSProperties = {
  marginTop: '10px',
  textAlign: 'left',
}

const columnDivStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '30% 70%',
  gap: '10px',
  marginBottom: '10px',
  borderBottom: '1px dotted #ccc',
  paddingBottom: '10px',
}

const columnTitleStyle: CSSProperties = {
  fontWeight: 'bold',
  fontSize: '12px',
}

const workerSectionStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
}

const hasQualifiedStyle: CSSProperties = {
  marginBottom: '20px',
  fontSize: '10px',
  fontWeight: 'bold',
  whiteSpace: 'nowrap',
}

const arrayStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '10px',
  rowGap: '10px',
}

const badgeStyle: CSSProperties = {
  backgroundColor: '#f5f5f5',
  padding: '5px 10px',
  borderRadius: '5px',
  fontSize: '12px',
}

const pendingStyle: CSSProperties = {
  ...badgeStyle,
  backgroundColor: '#eee',
  color: '#aaa',
}

const leaderStyle: CSSProperties = {
  position: 'absolute',
  top: '-15px',
  left: '-5px',
  fontSize: '13px',
  color: PRIMARY_COLOR,
  border: '1px solid',
  padding: '0 5px',
  borderRadius: '5px',
  backgroundColor: 'white',
  whiteSpace: 'nowrap',
}

const allianceSectionStyle: CSSProperties = {
  marginTop: '10px',
}

const allianceTextStyle: CSSProperties = {
  fontSize: '8px',
  color: 'gray',
  whiteSpace: 'nowrap',
}

const allianceBadgeStyle: CSSProperties = {
  padding: '5px 10px',
  borderRadius: '5px',
  fontSize: '12px',
  border: '0.5px solid #ccc',
}

const commentTitleStyle: CSSProperties = {
  fontWeight: 'bold',
  fontSize: '12px',
  whiteSpace: 'nowrap',
}

const commentContentStyle = {
  whiteSpace: 'pre-wrap',
  marginTop: '25px',
}

const responseFormContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
}

const radioGroupStyle = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  gap: '20px',
  marginBottom: '10px',
}

const radioStyle = {
  color: '#cccccc',
  '&.Mui-checked': {
    color: '#14b7ae',
  },
}

const textFieldStyle = {
  marginTop: '10px',
  width: '85vw',
}

const errorTextStyle = {
  color: 'red',
  marginTop: '10px',
}

const alertStyle: CSSProperties = {
  alignItems: 'center',
}

const floatingArrowContainerStyle: CSSProperties = {
  position: 'fixed',
  bottom: '20px',
  right: '20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '48px',
  height: '48px',
  borderRadius: '50%',
  backgroundColor: '#808080',
  animation: 'bounceArrow 2s ease-in-out infinite',
  cursor: 'pointer',
  zIndex: 1000,
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
}

const floatingArrowStyle: CSSProperties = {
  fontSize: '36px',
  color: '#ffffff',
}

const dialogConfirmTextStyle = {
  marginBottom: '10px',
}

const dialogButtonContainerStyle = {
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: '20px',
}

const dialogYesButtonStyle: CSSProperties = {
  minWidth: '60px',
}

const reportTextStyle: CSSProperties = {
  color: 'red',
}

const deletedCardContainerStyle: CSSProperties = {
  textAlign: 'center',
  padding: '30px 20px',
}

const deletedCardLabelStyle: CSSProperties = {
  fontSize: '14px',
  color: '#999',
  marginBottom: '10px',
  fontWeight: 'normal',
}

const deletedCardNameStyle: CSSProperties = {
  fontSize: '20px',
  fontWeight: 'bold',
  color: '#333',
  lineHeight: '1.6',
}
